import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ClientLayout } from "../components/ClientLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { FileText, Clock, CheckCircle, AlertCircle, ChevronRight, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const ClientDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const res = await api.get("/requests");
      setRequests(res.data);
    } catch (err) {
      toast.error("Failed to load requests");
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case "rejected":
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Clock className="w-5 h-5 text-orange-500" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "pending": return "Pending";
      case "documents_uploaded": return "Documents Uploaded";
      case "under_review": return "Under Review";
      case "completed": return "Review Completed";
      case "tax_filed": return "Tax Filed";
      case "closed": return "Closed";
      case "rejected": return "Rejected";
      default: return (status || "").replaceAll("_", " ");
    }
  };

  const getProgress = (status) => {
    const progressMap = {
      pending: 20,
      documents_uploaded: 40,
      under_review: 60,
      completed: 80,     // Review Completed
      tax_filed: 100,
      closed: 100,
      rejected: 60       // you can change to 100 if you want “final” look
    };

    return progressMap[status] ?? 20;
  };


  return (
    <ClientLayout>
      <div className="space-y-8">
        {/* Welcome Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Fraunces, serif' }}>Welcome, {user?.name}!</h1>
            <p className="opacity-70 mt-1">Track your CA Assisted tax filing progress and manage documents</p>
          </div>
          <Button
            onClick={() => navigate("/plans")}
            className="btn-primary flex items-center gap-2"
            data-testid="new-filing-btn"
          >
            <Plus size={20} />
            New Tax Filing
          </Button>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="stat-card">
            <p className="text-sm opacity-60 mb-1">Total Filings</p>
            <p className="text-3xl font-bold" style={{ fontFamily: 'Fraunces, serif' }}>{requests.length}</p>
          </div>
          <div className="stat-card">
            <p className="text-sm opacity-60 mb-1">In Progress</p>
            <p className="text-3xl font-bold" style={{ fontFamily: 'Fraunces, serif', color: 'var(--warning)' }}>
              {requests.filter(r => !['completed', 'rejected'].includes(r.status)).length}
            </p>
          </div>
          <div className="stat-card">
            <p className="text-sm opacity-60 mb-1">Completed</p>
            <p className="text-3xl font-bold" style={{ fontFamily: 'Fraunces, serif', color: 'var(--success)' }}>
              {requests.filter(r => r.status === 'completed').length}
            </p>
          </div>
          <div className="stat-card">
            <p className="text-sm opacity-60 mb-1">Pending Payment</p>
            <p className="text-3xl font-bold" style={{ fontFamily: 'Fraunces, serif', color: 'var(--error)' }}>
              {requests.filter(r => r.payment_status === 'unpaid').length}
            </p>
          </div>
        </div>

        {/* Active Requests */}
        <div>
          <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Fraunces, serif' }}>Your Tax Filings</h2>
          
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : requests.length === 0 ? (
            <div className="empty-state bg-white rounded-xl border" style={{ borderColor: 'var(--border)' }}>
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <h3 className="text-lg font-semibold mb-2">No Tax Filings Yet</h3>
              <p className="opacity-60 mb-4">Start your first tax filing by choosing a plan</p>
              <Button onClick={() => navigate("/plans")} className="btn-primary" data-testid="start-filing-btn">
                Choose a Plan
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {requests.map((request) => (
                <div
                  key={request.id}
                  className="bg-white rounded-xl border p-6"
                  style={{
                    borderColor: request.payment_status === 'unpaid' ? 'var(--accent)' : 'var(--border)',
                    borderWidth: request.payment_status === 'unpaid' ? '2px' : '1px',
                    cursor: request.payment_status === 'unpaid' ? 'default' : 'pointer'
                  }}
                  onClick={() => request.payment_status !== 'unpaid' && navigate(`/request/${request.id}`)}
                  data-testid={`request-card-${request.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        {getStatusIcon(request.status)}
                        <h3 className="text-lg font-semibold">{request.plan_name}</h3>
                      </div>
                      <p className="text-sm opacity-60">FY {request.financial_year} • {request.plan_type === 'salary' ? 'Salary' : 'Business'}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={`status-${request.payment_status}`}>
                        {request.payment_status === 'paid' ? 'Paid' : 'Payment Pending'}
                      </Badge>
                      {request.payment_status === 'paid' && <ChevronRight className="opacity-40" />}
                    </div>
                  </div>

                  {/* Progress Bar — only for paid cases */}
                  {request.payment_status === 'paid' && (
                    <div className="mb-2">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="capitalize">{getStatusLabel(request.status)}</span>
                        <span className="mono">{Math.round(getProgress(request.status))}%</span>
                      </div>
                      <div className="progress-bar h-2">
                        <div className="progress-fill" style={{ width: `${getProgress(request.status)}%` }}></div>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between text-sm opacity-60 mb-3">
                    <span>Created: {new Date(request.created_at).toLocaleDateString()}</span>
                    <span className="mono font-medium" style={{ color: 'var(--accent)' }}>₹{request.price.toLocaleString()}</span>
                  </div>

                  {/* Payment CTA for unpaid cases */}
                  {request.payment_status === 'unpaid' && (
                    <div className="flex gap-3 pt-3" style={{ borderTop: '1px solid var(--border)' }}>
                      <Button
                        className="btn-primary flex-1"
                        onClick={(e) => { e.stopPropagation(); navigate(`/request/${request.id}`); }}
                      >
                        Complete Payment
                      </Button>
                      <Button
                        variant="outline"
                        className="flex-shrink-0"
                        onClick={(e) => { e.stopPropagation(); navigate("/plans"); }}
                      >
                        Change Plan
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </ClientLayout>
  );
};

export default ClientDashboard;